// dataService.js
import { db } from '@/firebase';
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";

export function listenToTablesWithLeadingBids(callback) {
  const tablesCollection = collection(db, "tables");
  const auctionItemsCollection = collection(db, "auctionItems");

  let tables = [];
  let auctionItems = [];

  const computeAndEmit = () => {
    // Build a map from userId -> tableName using the users array on each table
    const userTableMap = {};
    tables.forEach(table => {
      if (Array.isArray(table.users)) {
        table.users.forEach(user => {
          if (user.id) userTableMap[user.id] = table.name;
        });
      }
    });

    // Count how many items each table is currently leading
    const leadCounts = {};
    tables.forEach(table => { leadCounts[table.name] = 0; });

    auctionItems.forEach(item => {
      if (item.latestBidderID) {
        const tableName = userTableMap[item.latestBidderID];
        if (tableName && Object.prototype.hasOwnProperty.call(leadCounts, tableName)) {
          leadCounts[tableName]++;
        }
      }
    });

    const updatedTables = tables
      .map(table => ({ ...table, leadingBids: leadCounts[table.name] || 0 }))
      .sort((a, b) => b.leadingBids - a.leadingBids);

    callback(updatedTables);
  };

  const unsubscribeTables = onSnapshot(tablesCollection, (snapshot) => {
    tables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    computeAndEmit();
  }, (error) => {
    console.error("Error fetching tables:", error);
  });

  const unsubscribeItems = onSnapshot(auctionItemsCollection, (snapshot) => {
    auctionItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    computeAndEmit();
  }, (error) => {
    console.error("Error fetching auction items:", error);
  });

  return () => {
    unsubscribeTables();
    unsubscribeItems();
  };
}

// Lyssnare för användare
export function listenToUsers(callback, retries = 3) {
  const usersCollection = collection(db, "users");
  const unsubscribe = onSnapshot(usersCollection,
    (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(users);
    },
    (error) => {
      console.error("Error fetching users: ", error);
      if (retries > 0) {
        console.log(`Retrying... attempts left: ${retries}`);
        listenToUsers(callback, retries - 1); // Retry with remaining attempts
      }
    }
  );
  return unsubscribe; // Return unsubscribe function to allow stopping the listener if needed
}

// Lyssnare för auktionsobjekt
export function listenToAuctionItems(callback, retries = 3) {
  const auctionItemsCollection = collection(db, "auctionItems");
  const unsubscribe = onSnapshot(auctionItemsCollection,
    (snapshot) => {
      const auctionItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(auctionItems);
    },
    (error) => {
      console.error("Error fetching auction items: ", error);
      if (retries > 0) {
        console.log(`Retrying... attempts left: ${retries}`);
        listenToAuctionItems(callback, retries - 1); // Retry with remaining attempts
      }
    }
  );
  return unsubscribe;
}

// Lyssnare för bord
export function listenToTables(callback, retries = 3) {
  const tablesCollection = collection(db, "tables");
  const unsubscribe = onSnapshot(tablesCollection,
    (snapshot) => {
      const tables = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const nameA = a.name.match(/\d+$/) ? parseInt(a.name.match(/\d+$/)[0], 10) : a.name;
          const nameB = b.name.match(/\d+$/) ? parseInt(b.name.match(/\d+$/)[0], 10) : b.name;

          // If both are numbers, sort by number
          if (typeof nameA === 'number' && typeof nameB === 'number') {
            return nameA - nameB;
          }

          // If only one is a number, prioritize it
          if (typeof nameA === 'number') return -1;
          if (typeof nameB === 'number') return 1;

          // Otherwise, sort lexicographically
          return a.name.localeCompare(b.name);
        });

      callback(tables);
    },
    (error) => {
      console.error("Error fetching tables: ", error);
      if (retries > 0) {
        console.log(`Retrying... attempts left: ${retries}`);
        listenToTables(callback, retries - 1);
      }
    }
  );
  return unsubscribe;
}

// Funktion för att lyssna på procentandelen av gäster som har lagt bud
export function listenToBidPercentage(callback) {
  listenToUsers(async (users) => {
    try {
      listenToAuctionItems(async (auctionItems) => {
        let usersWithBids = new Set();
        let totalBidsProcessed = 0;
        let totalAuctionItems = auctionItems.length;

        // Use Promise.all to ensure all auction items are processed before calculating percentage.
        // Includes both bids and donation purchases so flerexemplar-köp räknas in.
        await Promise.all(
          auctionItems.map(async (item) => {
            const bidsCollection = collection(db, `auctionItems/${item.id}/bids`);
            const purchasesCollection = collection(
              db,
              `auctionItems/${item.id}/purchase`
            );
            const collectUid = (snap) => {
              snap.docs.forEach((d) => {
                if (d.data().user?.uid) usersWithBids.add(d.data().user.uid);
              });
            };
            return Promise.all([
              new Promise((resolve, reject) => {
                onSnapshot(
                  bidsCollection,
                  (snap) => { collectUid(snap); resolve(); },
                  (error) => {
                    console.error(`Error fetching bids for auction item ${item.id}: `, error);
                    reject(error);
                  }
                );
              }),
              new Promise((resolve, reject) => {
                onSnapshot(
                  purchasesCollection,
                  (snap) => { collectUid(snap); resolve(); },
                  (error) => {
                    console.error(`Error fetching purchases for auction item ${item.id}: `, error);
                    reject(error);
                  }
                );
              }),
            ]).then(() => {
              totalBidsProcessed++;
            });
          })
        );

        if (totalBidsProcessed === totalAuctionItems) {
          const percentage = (usersWithBids.size / users.length) * 100;
          callback(percentage.toFixed(2));
        }
      });
    } catch (error) {
      console.error("Error calculating bid percentage: ", error);
    }
  });
}

// Funktion för att lyssna på antalet bud per bord.
//
// Räknar både:
//   - bud i `auctionItems/{id}/bids`            (vanliga auktionsobjekt)
//   - köp i `auctionItems/{id}/purchase`        (donationsobjekt, inkl.
//                                                flerexemplar-objekt med
//                                                fast pris och lager).
// Varje köp av ett donationsobjekt räknas som ett "bud" så att flerexemplar-
// försäljningar syns i topplistorna per bord.
export function listenToBidsPerTable(callback) {
  listenToTables(async (tables) => {
    listenToAuctionItems(async (auctionItems) => {
      let tableBids = {};
      let tableNames = {};

      // Initialize bid counts and names for each table
      tables.forEach(table => {
        tableBids[table.id] = 0;
        tableNames[table.id] = table.name;
      });

      const incrementForSnapshot = (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          const tableId = docSnap.data().user?.tableId;
          if (Object.prototype.hasOwnProperty.call(tableBids, tableId)) {
            tableBids[tableId]++;
          }
        });
      };

      // Use Promise.all to wait for all auction items to be processed
      await Promise.all(
        auctionItems.map(async (item) => {
          const bidsCollection = collection(db, `auctionItems/${item.id}/bids`);
          const purchasesCollection = collection(
            db,
            `auctionItems/${item.id}/purchase`
          );
          return Promise.all([
            new Promise((resolve, reject) => {
              onSnapshot(
                bidsCollection,
                (snap) => {
                  incrementForSnapshot(snap);
                  resolve();
                },
                (error) => {
                  console.error(
                    `Error fetching bids for auction item ${item.id}: `,
                    error
                  );
                  reject(error);
                }
              );
            }),
            new Promise((resolve, reject) => {
              onSnapshot(
                purchasesCollection,
                (snap) => {
                  incrementForSnapshot(snap);
                  resolve();
                },
                (error) => {
                  console.error(
                    `Error fetching purchases for auction item ${item.id}: `,
                    error
                  );
                  reject(error);
                }
              );
            }),
          ]);
        })
      );

      // Sort and send the top 5 tables with the most bids/purchases
      const sortedTableBids = Object.entries(tableBids)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([tableId, bidCount]) => ({
          name: tableNames[tableId],
          bidCount
        }));

      callback(sortedTableBids);
    });
  });
}

// Funktion för att lyssna på antalet objekt varje bord leder i
export function listenToLeadsPerTable(callback) {
  listenToTables(async (tables) => {
    listenToAuctionItems(async (auctionItems) => {
      let tableLeads = {};
      let tableNames = {};

      // Initialize lead counts and names for each table
      tables.forEach(table => {
        tableLeads[table.id] = 0;
        tableNames[table.id] = table.name;
      });

      for (const item of auctionItems) {
        const latestBidderId = item.latestBidderID;

        if (latestBidderId) {
          // Fetch the user document to ensure the user still exists
          const userDocRef = doc(db, "users", latestBidderId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
           

            // Iterate over each table and check if the user belongs to the table
            tables.forEach(table => {
              const userFound = table.users?.some(user => user.id === latestBidderId);
              if (userFound) {
                tableLeads[table.id]++;
              }
            });
          } else {
            console.warn(`User with ID ${latestBidderId} does not exist.`);
          }
        }
      }

      // Sort and send the top 10 tables leading the most items
      const sortedTableLeads = Object.entries(tableLeads)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([tableId, leadCount]) => ({
          name: tableNames[tableId],
          leadCount
        }));

      callback(sortedTableLeads);
    });
  });
}